import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTicketById,
  updateTicket,
  resolveTicket,
  deleteTicket,
  closeTicket,
  reopenTicket,
  startProgressTicket,
  holdTicket,
} from '@/api/tickets';
import {
  TicketDetails,
  UpdateTicketDto,
  ResolveTicketBody,
  TicketDictionariesResponse,
  TicketStatus,
  TicketPriority,
  TicketCategory,
} from '@/types/tickets';
import UIButton from '@/components/shared/UIButton';
import UIPageTitle from '@/components/shared/UIPageTitle';
import Input from '@/components/shared/Input';
import UISelect from '@/components/shared/UISelect';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import { useToast } from '@/components/shared/ToastProvider';
import TicketOverview from '@/components/dashboard/Ticket/TicketOverviewSection/TicketsOverviewSection'; // Direct import
import TicketEditSection from '@/components/dashboard/Ticket/TicketEditSection';

const TicketEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [isStartingProgress, setIsStartingProgress] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [resolutionComment, setResolutionComment] = useState<string>(''); // New state for resolution comment

  const { data: dictionaries, isLoading: isDictionariesLoading } = useGetDictionaries<TicketDictionariesResponse>('tickets');

  const statusOptions = useMemo(() => {
    if (!dictionaries?.ticketStatuses) return [];
    return dictionaries.ticketStatuses.map(s => ({ value: s.id, label: s.name }));
  }, [dictionaries?.ticketStatuses]);

  const categoryOptions = useMemo(() => {
    if (!dictionaries?.ticketCategories) return [];
    return dictionaries.ticketCategories.map(c => ({
      value: c,
      label: c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
    }));
  }, [dictionaries?.ticketCategories]);

  const priorityOptions = useMemo(() => {
    return [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ];
  }, []);

  useEffect(() => {
    const fetchTicket = async () => {
      if (!id) {
        setError('Ticket ID is missing.');
        setLoading(false);
        return;
      }
      try {
        const fetchedTicket = await getTicketById(id);
        setTicket(fetchedTicket);
      } catch (err) {
        setError('Failed to load ticket.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTicket(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleSaveChanges = async () => {
    if (!ticket || !id) return;
    setIsSaving(true);
    try {
      const updateData: UpdateTicketDto = {
        title: ticket.title,
        description: ticket.description || undefined,
        category: ticket.category as TicketCategory,
        status: ticket.status as TicketStatus,
        priority: ticket.priority as TicketPriority,
      };
      await updateTicket(id, updateData);
      showToast('Ticket updated successfully!', 'success');
      const updatedTicket = await getTicketById(id); // Re-fetch to get updated status
      setTicket(updatedTicket);
    } catch (err) {
      showToast('Failed to update ticket.', 'error');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this ticket?')) return;
    setIsDeleting(true);
    try {
      await deleteTicket(id);
      showToast('Ticket deleted successfully!', 'success');
      navigate('/dashboard/tickets'); // Redirect to tickets list
    } catch (err) {
      showToast('Failed to delete ticket.', 'error');
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResolveTicket = async (resolutionType: ResolveTicketBody['resolution']) => {
    if (!id || !ticket) return;

    const currentStatus = ticket.status.toUpperCase();

    if (currentStatus !== 'OPEN' && currentStatus !== 'IN_PROGRESS') {
      showToast('Ticket must be OPEN or IN_PROGRESS to be resolved.', 'warning');
      return;
    }

    setIsResolving(true);
    try {
      await resolveTicket(id, { resolution: resolutionType, resolutionComment });
      showToast(`Ticket resolved as ${resolutionType}!`, 'success');
      setResolutionComment(''); // Clear comment after resolving
      const updatedTicket = await getTicketById(id); // Re-fetch to get updated status
      setTicket(updatedTicket);
    } catch (err) {
      showToast('Failed to resolve ticket.', 'error');
      console.error(err);
    } finally {
      setIsResolving(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!id || !ticket) return;

    const currentStatus = ticket.status.toUpperCase();

    if (currentStatus !== 'RESOLVED') {
      showToast('Ticket must be RESOLVED to be closed.', 'warning');
      return;
    }

    setIsClosing(true);
    try {
      await closeTicket(id);
      showToast('Ticket closed successfully!', 'success');
      const updatedTicket = await getTicketById(id);
      setTicket(updatedTicket);
    } catch (err) {
      showToast('Failed to close ticket.', 'error');
      console.error(err);
    } finally {
      setIsClosing(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!id || !ticket) return;

    const currentStatus = ticket.status.toUpperCase();

    if (!['RESOLVED', 'CLOSED', 'ON_HOLD'].includes(currentStatus)) {
      showToast('Only RESOLVED, CLOSED, or ON_HOLD tickets can be reopened.', 'warning');
      return;
    }

    setIsReopening(true);
    try {
      await reopenTicket(id);
      showToast('Ticket reopened successfully!', 'success');
      const updatedTicket = await getTicketById(id);
      setTicket(updatedTicket);
    } catch (err) {
      showToast('Failed to reopen ticket.', 'error');
      console.error(err);
    } finally {
      setIsReopening(false);
    }
  };

  const handleStartProgressTicket = async () => {
    if (!id || !ticket) return;

    const currentStatus = ticket.status.toUpperCase();

    if (!['OPEN', 'ON_HOLD'].includes(currentStatus)) {
      showToast('Only OPEN or ON_HOLD tickets can start progress.', 'warning');
      return;
    }

    setIsStartingProgress(true);
    try {
      await startProgressTicket(id);
      showToast('Ticket progress started!', 'success');
      const updatedTicket = await getTicketById(id);
      setTicket(updatedTicket);
    } catch (err) {
      showToast('Failed to start progress on ticket.', 'error');
      console.error(err);
    } finally {
      setIsStartingProgress(false);
    }
  };

  const handleHoldTicket = async () => {
    if (!id || !ticket) return;

    const currentStatus = ticket.status.toUpperCase();

    if (!['OPEN', 'IN_PROGRESS'].includes(currentStatus)) {
      showToast('Only OPEN or IN_PROGRESS tickets can be put on hold.', 'warning');
      return;
    }

    setIsHolding(true);
    try {
      await holdTicket(id);
      showToast('Ticket put on hold!', 'success');
      const updatedTicket = await getTicketById(id);
      setTicket(updatedTicket);
    } catch (err) {
      showToast('Failed to put ticket on hold.', 'error');
      console.error(err);
    } finally {
      setIsHolding(false);
    }
  };

  if (loading || isDictionariesLoading) {
    return <div>Loading ticket details...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!ticket) {
    return <div>Ticket not found.</div>;
  }

  const showResolveActions = ticket.status.toUpperCase() === 'OPEN' || ticket.status.toUpperCase() === 'IN_PROGRESS';

  return (
    <>
      <UIPageTitle
        title={`Edit Ticket #${ticket.id}`}
        action={
          <UIButton
            outline={true}
            variant="secondary"
            size="medium"
            onClick={() => navigate('/dashboard/tickets')}
          >
            Back to Tickets
          </UIButton>
        }
      />


      <TicketOverview
        ticket={ticket}
        loading={loading}
      />

      <TicketEditSection
        ticket={ticket}
        dictionaries={dictionaries}
        loading={loading || isDictionariesLoading}
        onSave={handleSaveChanges}
        onClear={() => {
          // Логіка очищення форми
          if (ticket) {
            setTicket({ ...ticket });
          }
        }}
        onCloseTicket={handleCloseTicket}
        onReopenTicket={handleReopenTicket}
        onDeleteTicket={handleDeleteTicket}
        loadingStates={{
          saving: isSaving,
          closing: isClosing,
          reopening: isReopening,
          deleting: isDeleting,
        }}
      />


    </>
  );
};

export default TicketEditPage;